import React, { useEffect, useRef, useState } from "react";
import { TpmBar } from "../../home/chartKit";
import { HOME_SCALE } from "../../home/homeTheme";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import dayjs from "dayjs";
import { httpService } from "../../../../services/httpService.service";
import Filters from "./Filters";
import { Button } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { isAuthorized } from "../../../../services/authService";
import { RolesEnum } from "../../../../services/rolesEnum";

// Register required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const IPAnalysis = () => {
  const [data, setData] = useState([]);
  const chartRef = useRef(null);
  const [filters, setFilters] = useState({
    state: "",
    country: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = () => {
    const projectId = localStorage.getItem("currentProjectID");

    const startDate = localStorage.getItem("Contract-startDate");
    const endDate = localStorage.getItem("Contract-endDate");

    const params = {
      ProjectId: projectId,
      StartDate: filters.startDate || dayjs(startDate).startOf("month"),
      EndDate: filters.endDate || dayjs(endDate).endOf("month"),
      Country: filters.country,
      State: filters.state,
      ImplementingPartnerId: filters.implementingPartnerId || "",
    };

    const queryString = new URLSearchParams(params).toString();

    httpService.get(
      `/api/Site/ipAnalysis?${queryString}`,
      (res) => {
        setData(res.data?.siteVisitStateIPStatuses || []);
      },
      (err) => {
        // console.log(err);
      }
    );
  };

  // Extracting dynamic status keys and grouping them by implementing partner
  const staticColors = {
    notInitiated: "#999",
    inProgress: HOME_SCALE.fair,
    completed: HOME_SCALE.good,
    postponed: HOME_SCALE.critical,
  };

  const generateColor = (key) => {
    const hash = key
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `hsl(${hash % 360}, 70%, 60%)`; // Generates consistent color per status
  };

  // Extract all unique status keys
  const allStatusKeys = Array.from(
    new Set(
      data.flatMap((item) =>
        Object.keys(item).filter((key) => key !== "implementingPartnerName")
      )
    )
  );

  // Dynamically create datasets for each status
  const datasets = allStatusKeys.map((status) => ({
    label: status.replace(/([A-Z])/g, " $1").trim(), // Format key names
    data: data.map((item) => item[status] || 0),
    backgroundColor: staticColors[status] || generateColor(status),
    borderColor: staticColors[status] || generateColor(status),
    borderWidth: 1,
  }));

  // Create labels from implementing partner names
  const labels = data.map((item) => item.implementingPartnerName);

  const accessibilityDistributionData = {
    labels,
    datasets,
  };

  const accessibilityDistributionOptions = {
    responsive: true,
    indexAxis: "y", // Horizontal bars
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "Status of Site Visits per IP",
        font: {
          size: 18,
          weight: "600",
          style: "normal",
          family: "Poppins",
          lineHeight: "26px",
        },
        color: "#6A113C",
      },
    },
    scales: {
      x: {
        stacked: true, // Enable stacking for the X axis
      },
      y: {
        stacked: true, // Enable stacking for the Y axis
      },
    },
  };

  const handleExportExcel = () => {
    const projectId = localStorage.getItem("currentProjectID");
    const params = {
      projectId: projectId,
      startDate: filters.startDate || dayjs().startOf("month"),
      endDate: filters.endDate || dayjs().endOf("month"),
      ...(filters.state ? { country: filters.state } : {}),
      ...(filters.implementingPartnerId
        ? { implementingPartnerId: filters.implementingPartnerId }
        : {}),
      ipAnalysis: true,
      exportFilters: {
        includeIPAnalysis: true,
      },
    };

    httpService.post(
      `/api/Site/exportVisitDataSummary`,
      params,
      (response) => {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Implementing Partner (IP) Analysis.xlsx";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
      (err) => {
        console.error(err);
      },
      { responseType: "arraybuffer" }
    );
  };

  const handleExportPDF = () => {
    if (!chartRef.current) return;

    toPng(chartRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const pdf = new jsPDF();
        const imgWidth = 180; // Adjust the image width
        const imgHeight =
          (chartRef.current.offsetHeight / chartRef.current.offsetWidth) *
          imgWidth;

        pdf.addImage(dataUrl, "PNG", 15, 15, imgWidth, imgHeight);
        pdf.save("Implementing Partner (IP) Analysis.pdf");
      })
      .catch((err) => {
        console.error("Error exporting to PDF:", err);
      });
  };

  return (
    <>
      <div className="export-container row gap-2">
        <div className="col-12 col-lg-8">
          <Filters
            filters={filters}
            setFilters={setFilters}
            implementingPartner={true}
          />
        </div>
        {isAuthorized([
          RolesEnum.SystemAdmin,
          RolesEnum.ProjectManager,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
        ]) && (
          <div className="row gap-2 col-12 col-lg-4">
            <Button
              className="exportButtonTPM addButtonTPM  col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
            <Button
              className="exportButtonTPM addButtonTPM  col-12 col-md-5 col-lg-5"
              style={{
                padding: "5px 20px",
                fontSize: "13px",
                maxHeight: "35px",
                background: " var(--2, #6A113C) !important",
              }}
              icon={<DownloadOutlined />}
              onClick={handleExportPDF}
            >
              Export to PDF
            </Button>
          </div>
        )}
      </div>
      <div ref={chartRef} className="charts-container">
        <div
          className="chart-card"
          style={{ width: "100%", minHeight: "400px" }}
        >
          <TpmBar
            data={accessibilityDistributionData}
            options={accessibilityDistributionOptions}
          />
        </div>
      </div>
    </>
  );
};

export default IPAnalysis;
